import type { SentryConfig } from '../types';
import type * as SentryNS from '@sentry/react';

type SentryModule = typeof SentryNS;
type CaptureArgs = Parameters<SentryModule['captureException']>;

let scheduled = false;
let loaded: SentryModule | null = null;

// Errors thrown before @sentry/react finishes loading are buffered, then
// flushed once init completes. Capped so a tight error loop can't grow it
// unbounded.
const MAX_BUFFER = 50;
const buffer: CaptureArgs[] = [];

function scheduleIdle(cb: () => void): void {
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  if (typeof ric === 'function') ric(cb);
  else setTimeout(cb, 1);
}

async function loadAndInit(cfg: SentryConfig): Promise<void> {
  const Sentry = await import('@sentry/react');
  Sentry.init({
    dsn: cfg.dsn,
    environment: cfg.environment,
    // Build-time SPA version, NOT cfg.release (the moveek-web shell SHA). Source
    // maps upload under __APP_VERSION__; tagging events with the same value is
    // what lets stack traces de-minify. See vite.config.ts sentryPlugin.
    release: __APP_VERSION__,
    // Drop noise from in-app WebView injected scripts (e.g. the Zalo in-app
    // browser injects a `zaloJSV2` global its own inline script references).
    // These surface as ReferenceErrors with a null/<anonymous> stack via
    // window.onerror — they are not booking-SPA bugs and the app keeps running.
    // Stack-less injected errors mean we can't denyUrls them, so match the
    // message. See MVK-2471.
    ignoreErrors: [/zaloJSV2/],
    tracesSampleRate: cfg.environment === 'prd' ? 0.1 : 1.0,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
  loaded = Sentry;
  for (const args of buffer.splice(0)) Sentry.captureException(...args);
}

// Defers loading @sentry/react off the critical path: when a DSN is present we
// dynamic-import + init at idle, after first paint. No-op without a DSN (dev /
// unprovisioned), matching prior behavior.
export function initSentry(cfg: SentryConfig): void {
  if (scheduled) return;
  if (!cfg.dsn) return;
  scheduled = true;
  scheduleIdle(() => {
    loadAndInit(cfg).catch((err) => {
      scheduled = false; // allow a later retry
      console.error('[sentry] deferred init failed', err);
    });
  });
}

function captureException(...args: CaptureArgs): void {
  if (loaded) {
    loaded.captureException(...args);
    return;
  }
  if (buffer.length < MAX_BUFFER) buffer.push(args);
}

// Mutable facade object so call-sites keep `Sentry.captureException(...)`.
// ErrorBoundary is intentionally NOT re-exported here anymore — App.tsx uses
// the dependency-free local ErrorBoundary so @sentry/react stays out of the
// eager import graph.
export const Sentry: { captureException: (...args: CaptureArgs) => void } = {
  captureException,
};
